import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ClockShift, TimeClockResponse } from "@truckbuddy/contract";
import { apiRequest, authRequest } from "../lib/api";
import { useAppSession } from "../lib/shell";
import { readClockPosition } from "../lib/time-clock";
import { Dialog } from "./Dialog";

export function signOut(): void {
  // A full reload clears every cached screen belonging to the person who left.
  void authRequest("/auth/logout", {}).catch(() => {
    /* Preserve the existing sign-out behavior when the logout request fails. */
  }).finally(() => window.location.assign("/"));
}

const SignOutContext = createContext<(() => void) | null>(null);

export function useSignOut(): () => void {
  const requestSignOut = useContext(SignOutContext);
  if (!requestSignOut) throw new Error("Sign-out controls require SignOutProvider.");
  return requestSignOut;
}

/** A deliberate logout checks the current shift; it never creates a punch on its own. */
export function SignOutProvider({ children, onSignOut = signOut }: {
  children: ReactNode; onSignOut?: () => void;
}) {
  const { realRoles, user } = useAppSession();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [shift, setShift] = useState<ClockShift | null>(null);
  const [checking, setChecking] = useState(false);
  const [punching, setPunching] = useState(false);
  const [error, setError] = useState("");
  const [exception, setException] = useState(false);
  const [reason, setReason] = useState("");
  const attempt = useRef(0);
  const busy = useRef(false);
  const pendingPunch = useRef<{ shiftId: string; requestId: string } | null>(null);

  function close() {
    if (busy.current && punching) return;
    attempt.current++;
    setOpen(false); setChecking(false); setError("");
    busy.current = false;
  }

  async function checkAndSignOut() {
    if (busy.current) return;
    if (!realRoles.includes("mechanic") || realRoles.some((role) => role === "admin" || role === "manager")) { onSignOut(); return; }
    busy.current = true;
    const currentAttempt = ++attempt.current;
    setShift(null); setOpen(true); setChecking(true); setError(""); setException(false); setReason("");
    try {
      const data = await apiRequest<TimeClockResponse>("/time-clock");
      if (attempt.current !== currentAttempt) return;
      if (!data.activeShift) { setOpen(false); onSignOut(); return; }
      setShift(data.activeShift);
    } catch (failure) {
      if (attempt.current === currentAttempt) setError(`Could not check your active shift. ${failure instanceof Error ? failure.message : "Try again."}`);
    } finally {
      if (attempt.current === currentAttempt) { setChecking(false); busy.current = false; }
    }
  }

  async function punchOutAndSignOut() {
    if (!shift || busy.current) return;
    busy.current = true; setPunching(true); setError("");
    try {
      const position = exception ? null : await readClockPosition();
      if (!pendingPunch.current || pendingPunch.current.shiftId !== shift.id) {
        pendingPunch.current = { shiftId: shift.id, requestId: crypto.randomUUID() };
      }
      await apiRequest("/time-clock/punch", { method: "POST", body: {
        action: "out", locationId: shift.locationId, shiftId: shift.id,
        requestId: pendingPunch.current.requestId, position,
        ...(exception ? { exceptionReason: reason.trim() } : {}),
      } });
      pendingPunch.current = null;
      void client.invalidateQueries({ queryKey: ["time-clock", user.userId] });
      void client.invalidateQueries({ queryKey: ["time-clock-team", user.userId] });
      void client.invalidateQueries({ queryKey: ["time-clock-eligibility", user.userId] });
      setOpen(false); onSignOut();
    } catch (failure) {
      setError(`${failure instanceof Error ? failure.message : "Could not punch out."} You are still signed in. Retry or choose to sign out without punching out.`);
    } finally { busy.current = false; setPunching(false); }
  }

  return <SignOutContext.Provider value={() => { void checkAndSignOut(); }}>
    {children}
    {open && <Dialog title="Punch out before signing out?" onClose={close} width={560}
      actions={<>
        <button type="button" className="btn btn-secondary" disabled={punching} onClick={close}>Cancel</button>
        <button type="button" className="btn btn-secondary" disabled={checking || punching}
          onClick={() => { attempt.current++; setOpen(false); onSignOut(); }}>Sign out without punching out</button>
        {shift && <button type="button" className="btn btn-primary"
          disabled={checking || punching || (exception && reason.trim().length < 5)}
          onClick={() => { void punchOutAndSignOut(); }}>{punching ? "Punching out..." : "Punch out and sign out"}</button>}
      </>}>
      {checking ? <p role="status">Checking your active shift...</p> : shift ? <>
        <p>You are punched in at {shift.locationName}. Sign-out alone keeps your shift running.</p>
        <p>Your location is checked only if you choose to punch out.</p>
        <details><summary>Need a location exception?</summary>
          <p>If you have left the worksite or cannot access location, record why. This punch-out will be marked as an exception for review.</p>
          <label className="clock-field">Punch-out exception reason<textarea maxLength={500} value={reason} disabled={punching} onChange={(event) => setReason(event.currentTarget.value)} /></label>
          <label><input type="checkbox" checked={exception} disabled={punching} onChange={(event) => setException(event.currentTarget.checked)} /> Punch out without a location reading</label>
        </details>
      </> : <p>Your shift status could not be confirmed. Signing out does not punch you out.</p>}
      {error && <p role="alert">{error}</p>}
      {!checking && !shift && <button type="button" className="btn btn-secondary" onClick={() => { void checkAndSignOut(); }}>Retry shift check</button>}
    </Dialog>}
  </SignOutContext.Provider>;
}
